import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Center,
  Spinner,
  Icon,
  chakra,
} from '@chakra-ui/react';
import {
  HiPlus,
  HiClock,
  HiCalendarDays,
  HiPrinter,
  HiCheckCircle,
  HiXMark,
} from 'react-icons/hi2';
import {
  Button,
  SelectInput,
  InputField,
  DialogComponent,
  StatusBadge,
  ToggleButton,
} from '@themes/aegix';
import { useMyStudents } from '../hooks/useReunification';
import {
  guardianStudentName,
  type GuardianStudentWithLegacyIds,
} from '../services/reunification';
import {
  useStudentCheckEvents,
  useCreateStudentCheckEvent,
  useCreateTardySlip,
} from '../hooks/useVisitorManagement';
import { getTardySlipHtml } from '../services/visitorManagement';
import { toaster } from './ui/toaster';

// Helper to format ISO date/time
const formatDateTime = (isoString: string) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
};

export function StudentCheckInView() {
  const { t } = useTranslation();
  const { data: studentsData, isLoading: loadingStudents } = useMyStudents();
  const { data: eventsData, isLoading: loadingEvents } = useStudentCheckEvents();

  const createEventMutation = useCreateStudentCheckEvent();
  const createTardySlipMutation = useCreateTardySlip();

  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [eventType, setEventType] = useState<'late_arrival' | 'early_dismissal'>('late_arrival');
  const [reason, setReason] = useState('');
  const [parentNotified, setParentNotified] = useState(false);
  const [teacherNotified, setTeacherNotified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tardy Slip Dialog State
  const [isTardySlipOpen, setIsTardySlipOpen] = useState(false);
  const [selectedEventPublicId, setSelectedEventPublicId] = useState('');
  const [classPeriod, setClassPeriod] = useState('');
  const [isGeneratingTardySlip, setIsGeneratingTardySlip] = useState(false);

  // The real guardian/students payload emits no `id`/`school_id`,
  // so this flow's invalid-data guard fires until a join key ships.
  const students: GuardianStudentWithLegacyIds[] = studentsData?.results ?? [];
  const events = eventsData?.results ?? [];

  const handleOpenCheckIn = () => {
    if (students.length > 0) {
      setSelectedStudentId(String(students[0].id));
    }
    setEventType('late_arrival');
    setReason('');
    setParentNotified(false);
    setTeacherNotified(false);
    setIsCheckInOpen(true);
  };

  const handlePrintTardySlip = async (eventPublicId: string) => {
    try {
      const html = await getTardySlipHtml(eventPublicId);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (error) {
      console.error('Failed to print tardy slip:', error);
      toaster.create({
        title: t('studentCheck.printErrorTitle'),
        description: t('studentCheck.printErrorDesc'),
        type: 'error',
      });
    }
  };

  const handleOpenTardySlipDialog = (eventPublicId: string) => {
    setSelectedEventPublicId(eventPublicId);
    setClassPeriod('');
    setIsTardySlipOpen(true);
  };

  const handleTardySlipSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEventPublicId) return;

    setIsGeneratingTardySlip(true);
    try {
      await createTardySlipMutation.mutateAsync({
        event_public_id: selectedEventPublicId,
        data: { class_period: classPeriod },
      });

      toaster.create({
        title: t('studentCheck.tardySlipCreatedTitle'),
        description: t('studentCheck.tardySlipCreatedDesc'),
        type: 'success',
      });
      
      // Print the newly created tardy slip
      handlePrintTardySlip(selectedEventPublicId);
      setIsTardySlipOpen(false);
    } catch {
      toaster.create({
        title: t('studentCheck.tardySlipErrorTitle'),
        description: t('studentCheck.tardySlipErrorDesc'),
        type: 'error',
      });
    } finally {
      setIsGeneratingTardySlip(false);
    }
  };

  const handleCheckInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      toaster.create({
        title: t('studentCheck.studentRequiredTitle'),
        description: t('studentCheck.studentRequiredDesc'),
        type: 'error',
      });
      return;
    }

    const student = students.find((s) => String(s.id) === selectedStudentId);
    if (!student || student.school_id === undefined || student.id === undefined) {
      toaster.create({
        title: t('studentCheck.invalidDataTitle'),
        description: t('studentCheck.invalidDataDesc'),
        type: 'error',
      });
      return;
    }

    if (!reason.trim()) {
      toaster.create({
        title: t('studentCheck.reasonRequiredTitle'),
        description: t('studentCheck.reasonRequiredDesc'),
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const createdEvent = await createEventMutation.mutateAsync({
        student: student.id,
        school: student.school_id,
        event_type: eventType,
        reason: reason.trim(),
        timestamp: new Date().toISOString(),
        parent_notified: parentNotified,
        teacher_notified: teacherNotified,
      });

      toaster.create({
        title: eventType === 'late_arrival' ? t('studentCheck.lateArrivalRegisteredTitle') : t('studentCheck.earlyDismissalRegisteredTitle'),
        description: t('studentCheck.recordedSuccessDesc', { name: guardianStudentName(student) }),
        type: 'success',
      });
      
      setIsCheckInOpen(false);

      // If late arrival, prompt to generate tardy slip
      if (eventType === 'late_arrival' && createdEvent?.public_id) {
        handleOpenTardySlipDialog(createdEvent.public_id);
      }
    } catch {
      toaster.create({
        title: t('studentCheck.submitErrorTitle'),
        description: t('studentCheck.submitErrorDesc'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const studentOptions = students.map((s) => ({
    label: guardianStudentName(s),
    value: String(s.id),
  }));

  const eventTypeOptions = [
    { label: t('studentCheck.lateArrival'), value: 'late_arrival' },
    { label: t('studentCheck.earlyDismissal'), value: 'early_dismissal' },
  ];

  const isLoading = loadingStudents || loadingEvents;

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Text fontSize="lg" fontWeight="600" color="text.primary">
            {t('studentCheck.title')}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('studentCheck.subtitle')}
          </Text>
        </Box>
        <Button
          visual="primary"
          onClick={handleOpenCheckIn}
          icon={<HiPlus />}
          disabled={students.length === 0}
          data-e2e="student-checkin-btn"
        >
          {t('studentCheck.addBtn')}
        </Button>
      </HStack>

      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" />
        </Center>
      ) : events.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {events.map((event) => {
            const isLate = event.event_type === 'late_arrival';
            const badgeStatus = isLate ? 'warning' : 'info';
            const badgeLabel = isLate ? t('studentCheck.lateArrival') : t('studentCheck.earlyDismissal');
            const hasTardySlip = event.tardy_slips && event.tardy_slips.length > 0;
            const tardySlip = hasTardySlip ? event.tardy_slips![0] : null;

            return (
              <Box
                key={event.public_id}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                p={5}
                position="relative"
                data-e2e={`checkin-card-${event.public_id}`}
              >
                <HStack justifyContent="space-between" mb={3} alignItems="flex-start">
                  <VStack align="stretch" gap={1}>
                    <Text fontSize="md" fontWeight="600" color="text.primary">
                      {event.student_name}
                    </Text>
                    <HStack fontSize="xs" color="text.secondary" gap={1.5}>
                      <Icon as={HiClock} />
                      <Text>{formatDateTime(event.timestamp)}</Text>
                    </HStack>
                  </VStack>
                  <StatusBadge status={badgeStatus} label={badgeLabel} />
                </HStack>

                <Box bg="surface.secondary" p={3} borderRadius="md" mb={4}>
                  <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                    {t('studentCheck.reason')}
                  </Text>
                  <Text fontSize="sm" color="text.primary">
                    {event.reason || t('studentCheck.noReason')}
                  </Text>
                </Box>

                <SimpleGrid columns={2} gap={2} mb={4} fontSize="xs" color="text.secondary">
                  <HStack>
                    <Icon
                      as={HiCheckCircle}
                      color={event.parent_notified ? 'green.500' : 'gray.300'}
                    />
                    <Text>{t('studentCheck.parentNotified')}</Text>
                  </HStack>
                  <HStack>
                    <Icon
                      as={HiCheckCircle}
                      color={event.teacher_notified ? 'green.500' : 'gray.300'}
                    />
                    <Text>{t('studentCheck.teacherNotified')}</Text>
                  </HStack>
                </SimpleGrid>

                {isLate && (
                  <Box
                    pt={3}
                    borderTop="1px solid"
                    borderColor="border.secondary"
                    mt={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color="text.secondary">
                        {t('studentCheck.tardySlip')}
                      </Text>
                      <Text fontSize="sm" color="text.primary">
                        {hasTardySlip
                          ? t('studentCheck.classPeriodLabel', { period: tardySlip?.class_period || 'N/A' })
                          : t('studentCheck.notGenerated')}
                      </Text>
                    </Box>
                    {hasTardySlip ? (
                      <Button
                        visual="secondary"
                        size="sm"
                        icon={<HiPrinter />}
                        onClick={() => handlePrintTardySlip(event.public_id)}
                        data-e2e={`print-tardy-slip-${event.public_id}`}
                      >
                        {t('studentCheck.printSlip')}
                      </Button>
                    ) : (
                      <Button
                        visual="secondary"
                        size="sm"
                        icon={<HiPlus />}
                        onClick={() => handleOpenTardySlipDialog(event.public_id)}
                        data-e2e={`generate-tardy-slip-btn-${event.public_id}`}
                      >
                        {t('studentCheck.generateSlip')}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      ) : (
        <Center py={12} border="1px dashed" borderColor="border.secondary" borderRadius="lg">
          <VStack gap={2}>
            <HiCalendarDays size={32} />
            <Text fontSize="md" fontWeight="600" color="text.secondary">
              {t('studentCheck.empty')}
            </Text>
            <Text fontSize="sm" color="text.secondary" textAlign="center" maxW="400px">
              {students.length === 0
                ? t('studentCheck.noStudentsNotice')
                : t('studentCheck.noEventsNotice')}
            </Text>
          </VStack>
        </Center>
      )}

      {/* Main Check-In Dialog */}
      <DialogComponent
        isOpen={isCheckInOpen}
        onClose={() => setIsCheckInOpen(false)}
        headingText={t('studentCheck.dialogTitle')}
        e2eTitle="student-checkin-dialog"
      >
        <form onSubmit={handleCheckInSubmit}>
          <VStack gap={4} align="stretch" mt={4}>
            <SelectInput
              label={t('studentCheck.selectStudent')}
              name="student"
              option={studentOptions}
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              e2eTitle="checkin-student-select"
              searchPlaceholder={t('searchPlaceholder')}
              required
            />

            <SelectInput
              label={t('studentCheck.selectEventType')}
              name="event_type"
              option={eventTypeOptions}
              value={eventType}
              onChange={(val) => setEventType(val as 'late_arrival' | 'early_dismissal')}
              e2eTitle="checkin-type-select"
              required
            />

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('studentCheck.reasonLabel')}
              </chakra.label>
              <InputField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('studentCheck.reasonPlaceholder')}
                required
                data-e2e="checkin-reason-input"
              />
            </Box>

            <VStack gap={3} align="stretch" py={2} borderTop="1px solid" borderBottom="1px solid" borderColor="border.secondary">
              <ToggleButton
                title={t('studentCheck.parentNotified')}
                checked={parentNotified}
                onCheckedChange={(details: { checked: boolean }) => setParentNotified(details.checked)}
                e2eTitle="checkin-parent-notified-toggle"
              />

              <ToggleButton
                title={t('studentCheck.teacherNotified')}
                checked={teacherNotified}
                onCheckedChange={(details: { checked: boolean }) => setTeacherNotified(details.checked)}
                e2eTitle="checkin-teacher-notified-toggle"
              />
            </VStack>

            <HStack justifyContent="flex-end" gap={3} mt={4}>
              <Button visual="secondary" onClick={() => setIsCheckInOpen(false)} type="button">
                {t('studentCheck.cancel')}
              </Button>
              <Button visual="primary" type="submit" disabled={isSubmitting} data-e2e="checkin-submit-btn">
                {isSubmitting ? t('studentCheck.submitting') : t('studentCheck.submit')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </DialogComponent>

      {/* Tardy Slip Generation Dialog */}
      <DialogComponent
        isOpen={isTardySlipOpen}
        onClose={() => setIsTardySlipOpen(false)}
        headingText={t('studentCheck.tardyDialogTitle')}
        e2eTitle="tardy-slip-dialog"
      >
        <form onSubmit={handleTardySlipSubmit}>
          <VStack gap={4} align="stretch" mt={4}>
            <Text fontSize="sm" color="text.secondary">
              {t('studentCheck.tardyInstructions')}
            </Text>

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('studentCheck.classPeriodInputLabel')}
              </chakra.label>
              <InputField
                value={classPeriod}
                onChange={(e) => setClassPeriod(e.target.value)}
                placeholder={t('studentCheck.classPeriodPlaceholder')}
                data-e2e="tardy-slip-class-period-input"
              />
            </Box>

            <HStack justifyContent="flex-end" gap={3} mt={4}>
              <Button visual="secondary" onClick={() => setIsTardySlipOpen(false)} type="button">
                {t('studentCheck.cancel')}
              </Button>
              <Button visual="primary" type="submit" disabled={isGeneratingTardySlip} data-e2e="tardy-slip-submit-btn">
                {isGeneratingTardySlip ? t('studentCheck.generating') : t('studentCheck.generateAndPrint')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </DialogComponent>
    </Box>
  );
}
